/**
 * Summary scene - End of session reflection
 *
 * Design principles (government/educational):
 * - Calm resolution, not celebration
 * - Observational patterns, not judgments
 * - Reinforce learning without scoring
 * - User agency without coercion
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BaseScene, type SceneContext } from './BaseScene';
import { Button, Panel } from '@ui/index';
import { theme } from '@core/theme';
import { app } from '@core/Application';
import { stateMachine } from '@core/StateMachine';
import type { DecisionRecord } from '@core/types';

const ROLE_LABELS: Record<string, string> = {
  pedestrian: 'Pedestrian',
  cyclist: 'Cyclist',
  driver: 'Driver',
};

// Decision pattern categories based on decision IDs
const PATTERN_KEYWORDS = {
  cautious: ['wait', 'stop', 'yield', 'slow', 'check'],
  communicative: ['eye-contact', 'signal', 'wave', 'acknowledge', 'gesture'],
  rushing: ['proceed', 'immediately', 'quickly', 'run', 'hurry', 'go'],
  observant: ['look', 'scan', 'observe', 'watch', 'assess'],
};

export class SummaryScene extends BaseScene {
  private content!: Container;
  private scrollOffset = 0;

  constructor(context: SceneContext) {
    super(context);
  }

  async init(): Promise<void> {
    // Background - calm, neutral
    const bg = new Graphics();
    bg.rect(0, 0, this.context.width, this.context.height);
    bg.fill(theme.colors.primaryBg);
    this.addChild(bg);

    // Content container
    this.content = new Container();
    this.addChild(this.content);

    const session = stateMachine.getSessionState();

    // Check if this is final completion (all 3 phases done)
    const isFinalCompletion = session.currentPhase >= 3;

    if (isFinalCompletion) {
      this.renderExperienceSummary();
    } else {
      this.renderPhaseSummary();
    }

    this.positionContent();
  }

  /**
   * Render phase-by-phase summary (phases 1 & 2)
   */
  private renderPhaseSummary(): void {
    const session = stateMachine.getSessionState();
    const decisionHistory = stateMachine.getDecisionHistory();

    // Title - observational, not evaluative
    const title = new Text({
      text: `Phase ${session.currentPhase} — Practice Complete`,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes['3xl'],
        fontWeight: theme.typography.weights.semibold,
        fill: theme.colors.textPrimary,
      }),
    });
    title.anchor.set(0.5, 0);
    this.content.addChild(title);

    // Subtitle
    const roleName = ROLE_LABELS[session.selectedRole || 'pedestrian'];
    const subtitle = new Text({
      text: `${roleName} Situational Awareness`,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.base,
        fill: theme.colors.textSecondary,
      }),
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(0, 44);
    this.content.addChild(subtitle);

    // Summary panel
    const summaryPanel = new Panel({
      width: Math.min(500, this.context.width - 64),
      severity: 'neutral',
    });

    summaryPanel.addTitle('What You Practiced');
    summaryPanel.addText(`${session.completedScenarios.length} scenarios explored`);
    summaryPanel.addText(`${decisionHistory.length} decisions made`);

    // Brief pattern observation
    const patterns = this.analyzeDecisionPatterns(decisionHistory);
    if (patterns.primary) {
      summaryPanel.addSpacer(12);
      summaryPanel.addText(patterns.primary);
    }

    summaryPanel.addSpacer(16);
    summaryPanel.addTitle('Key Principle');
    summaryPanel.addPrinciple(this.getPhaseKeyPrinciple(session.currentPhase));

    const panelDims = summaryPanel.getDimensions();
    summaryPanel.position.set(-panelDims.width / 2, 100);
    this.content.addChild(summaryPanel);

    // Action buttons - non-coercive
    const buttonsY = 100 + panelDims.height + 32;

    const revisitBtn = new Button({
      label: 'Revisit Phase',
      width: 140,
      height: 44,
      variant: 'secondary',
    });
    revisitBtn.position.set(-155, buttonsY);
    revisitBtn.setOnClick(() => this.handleRevisit());
    this.content.addChild(revisitBtn);

    const continueBtn = new Button({
      label: 'Continue',
      width: 140,
      height: 44,
      variant: 'primary',
    });
    continueBtn.position.set(15, buttonsY);
    continueBtn.setOnClick(() => this.handleContinue());
    this.content.addChild(continueBtn);

    // Alternative action link
    this.addActionLink('Try a different role', buttonsY + 60, () => {
      stateMachine.resetSession();
      app.navigateTo('role-select');
    });
  }

  /**
   * Render final experience summary (all phases complete)
   * Follows educational design: patterns observed, not scores
   */
  private renderExperienceSummary(): void {
    const session = stateMachine.getSessionState();
    const decisionHistory = stateMachine.getDecisionHistory();
    const roleName = ROLE_LABELS[session.selectedRole || 'pedestrian'];

    // Title - calm, reflective
    const title = new Text({
      text: 'What You Practiced',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes['3xl'],
        fontWeight: theme.typography.weights.semibold,
        fill: theme.colors.textPrimary,
      }),
    });
    title.anchor.set(0.5, 0);
    this.content.addChild(title);

    // Subtitle
    const subtitle = new Text({
      text: `${roleName} Road Safety — All Phases`,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.base,
        fill: theme.colors.textSecondary,
      }),
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(0, 44);
    this.content.addChild(subtitle);

    // Main summary panel
    const panelWidth = Math.min(540, this.context.width - 48);
    const summaryPanel = new Panel({
      width: panelWidth,
      severity: 'neutral',
    });

    // Section 1: Decision Patterns Observed
    summaryPanel.addTitle('Decision Patterns Observed');
    const patterns = this.analyzeDecisionPatterns(decisionHistory);

    patterns.tendencies.forEach(tendency => {
      summaryPanel.addText(`• ${tendency}`);
    });

    // Section 2: Strengths Demonstrated
    summaryPanel.addSpacer(16);
    summaryPanel.addTitle('Strengths Demonstrated');
    const strengths = this.identifyStrengths(decisionHistory, session.selectedRole || 'pedestrian');
    strengths.forEach(strength => {
      summaryPanel.addText(`• ${strength}`);
    });

    // Section 3: Situations Worth Re-Practicing (only if applicable)
    const revisitSuggestions = this.getSuggestedRevisits(decisionHistory, session.selectedRole || 'pedestrian');
    if (revisitSuggestions.length > 0) {
      summaryPanel.addSpacer(16);
      summaryPanel.addTitle('Situations Worth Re-Practicing');
      revisitSuggestions.forEach(suggestion => {
        summaryPanel.addText(`• ${suggestion}`);
      });
    }

    // Section 4: Key Safety Principles Reinforced
    summaryPanel.addSpacer(16);
    summaryPanel.addTitle('Key Safety Principles Reinforced');
    const principles = this.getRolePrinciples(session.selectedRole || 'pedestrian');
    principles.forEach(principle => {
      summaryPanel.addPrinciple(principle);
      summaryPanel.addSpacer(4);
    });

    const panelDims = summaryPanel.getDimensions();
    summaryPanel.position.set(-panelWidth / 2, 90);
    this.content.addChild(summaryPanel);

    // Action section - "What would you like to do next?"
    const actionsY = 90 + panelDims.height + 28;

    const actionsLabel = new Text({
      text: 'What would you like to do next?',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fill: theme.colors.textMuted,
      }),
    });
    actionsLabel.anchor.set(0.5, 0);
    actionsLabel.position.set(0, actionsY);
    this.content.addChild(actionsLabel);

    const buttonsY = actionsY + 32;

    // Revisit scenarios button
    const revisitBtn = new Button({
      label: 'Revisit Scenarios',
      width: 150,
      height: 44,
      variant: 'secondary',
    });
    revisitBtn.position.set(-240, buttonsY);
    revisitBtn.setOnClick(() => {
      // Reset to phase 1 but keep role
      stateMachine.clearHistory();
      app.navigateTo('scenario');
    });
    this.content.addChild(revisitBtn);

    // Try another role button
    const roleBtn = new Button({
      label: 'Try Another Role',
      width: 150,
      height: 44,
      variant: 'secondary',
    });
    roleBtn.position.set(-75, buttonsY);
    roleBtn.setOnClick(() => {
      stateMachine.resetSession();
      app.navigateTo('role-select');
    });
    this.content.addChild(roleBtn);

    // End session button
    const endBtn = new Button({
      label: 'End Session',
      width: 150,
      height: 44,
      variant: 'primary',
    });
    endBtn.position.set(90, buttonsY);
    endBtn.setOnClick(() => {
      stateMachine.resetSession();
      app.navigateTo('landing');
    });
    this.content.addChild(endBtn);

  }

  /**
   * Analyze decision patterns from history
   */
  private analyzeDecisionPatterns(history: ReadonlyArray<DecisionRecord>): {
    primary: string | null;
    tendencies: string[];
  } {
    if (history.length === 0) {
      return { primary: null, tendencies: ['Limited data from this session.'] };
    }

    // Count pattern occurrences
    const patternCounts = {
      cautious: 0,
      communicative: 0,
      rushing: 0,
      observant: 0,
    };

    history.forEach(record => {
      const decisionId = record.decisionId.toLowerCase();

      Object.entries(PATTERN_KEYWORDS).forEach(([pattern, keywords]) => {
        if (keywords.some(keyword => decisionId.includes(keyword))) {
          patternCounts[pattern as keyof typeof patternCounts]++;
        }
      });
    });

    const tendencies: string[] = [];
    const total = history.length;

    // Generate observational statements (not judgments)
    if (patternCounts.cautious > total * 0.4) {
      tendencies.push('You often waited for confirmation before proceeding.');
    }
    if (patternCounts.communicative > total * 0.3) {
      tendencies.push('You frequently sought visual confirmation from other road users.');
    }
    if (patternCounts.rushing > total * 0.3) {
      tendencies.push('In some situations, you relied on signals over direct observation.');
    }
    if (patternCounts.observant > total * 0.3) {
      tendencies.push('You demonstrated awareness of surrounding traffic conditions.');
    }

    // Calculate average decision time
    const avgTime = history.reduce((sum, d) => sum + d.timeToDecide, 0) / total;
    if (avgTime < 3000) {
      tendencies.push('Your decisions were made quickly, often under 3 seconds.');
    } else if (avgTime > 8000) {
      tendencies.push('You took time to consider options before deciding.');
    }

    // Fallback if no patterns detected
    if (tendencies.length === 0) {
      tendencies.push('Your responses varied across different situations.');
    }

    // Primary observation for phase summary
    const primary = tendencies.length > 0 ? tendencies[0] : null;

    return { primary, tendencies };
  }

  /**
   * Identify strengths demonstrated (positive framing)
   */
  private identifyStrengths(history: ReadonlyArray<DecisionRecord>, role: string): string[] {
    const strengths: string[] = [];

    // Look for positive patterns in decisions
    const hasEyeContact = history.some(r => r.decisionId.includes('eye-contact'));
    const hasWaited = history.some(r => r.decisionId.includes('wait'));
    const hasSignaled = history.some(r => r.decisionId.includes('signal'));
    const hasChecked = history.some(r => r.decisionId.includes('check') || r.decisionId.includes('look'));

    if (hasEyeContact) {
      strengths.push('Recognized the value of direct communication with other road users.');
    }
    if (hasWaited) {
      strengths.push('Showed patience in ambiguous situations.');
    }
    if (hasSignaled && role === 'cyclist') {
      strengths.push('Used signaling to communicate intentions.');
    }
    if (hasChecked) {
      strengths.push('Demonstrated active observation of surroundings.');
    }

    // Completion is itself a strength
    if (history.length >= 9) {
      strengths.push('Engaged with all phases of the training experience.');
    }

    // Fallback
    if (strengths.length === 0) {
      strengths.push('Completed multiple decision-making scenarios.');
    }

    return strengths.slice(0, 3); // Limit to 3 strengths
  }

  /**
   * Suggest scenarios worth re-practicing (gentle, non-judgmental)
   */
  private getSuggestedRevisits(history: ReadonlyArray<DecisionRecord>, role: string): string[] {
    const suggestions: string[] = [];

    // Only suggest if there were rushing patterns
    const rushingDecisions = history.filter(r =>
      r.decisionId.includes('proceed') ||
      r.decisionId.includes('immediately') ||
      r.decisionId.includes('quickly')
    );

    if (rushingDecisions.length > history.length * 0.4) {
      if (role === 'pedestrian') {
        suggestions.push('Crosswalk scenarios with turning vehicles may benefit from another look.');
      } else if (role === 'cyclist') {
        suggestions.push('Intersection scenarios with multiple traffic streams could be revisited.');
      } else {
        suggestions.push('Scenarios involving vulnerable road users may be worth exploring again.');
      }
    }

    // Check for quick decisions that might benefit from more consideration
    const veryQuickDecisions = history.filter(r => r.timeToDecide < 2000);
    if (veryQuickDecisions.length > history.length * 0.5) {
      suggestions.push('Some rapid decisions might benefit from additional reflection.');
    }

    return suggestions.slice(0, 2); // Limit suggestions
  }

  /**
   * Get role-specific safety principles
   */
  private getRolePrinciples(role: string): string[] {
    const principles: Record<string, string[]> = {
      pedestrian: [
        'Signals indicate right of way, but not guaranteed safety.',
        'Eye contact reduces uncertainty between you and drivers.',
        'Slowing down increases available reaction time for everyone.',
      ],
      cyclist: [
        'Visibility is your primary defense on shared roads.',
        'Clear signaling communicates your intentions to others.',
        'Positioning affects how well drivers can see and predict your path.',
      ],
      driver: [
        'Blind spots require active checking, not assumptions.',
        'Pedestrians and cyclists may not behave predictably.',
        'Patience at intersections prevents irreversible consequences.',
      ],
    };

    return principles[role] || principles.pedestrian;
  }

  /**
   * Get phase-specific key principle
   */
  private getPhaseKeyPrinciple(phase: number): string {
    const principles: Record<number, string> = {
      1: 'Signals give you the right of way, but they don\'t guarantee other road users have seen you.',
      2: 'Complex situations require active observation, not passive reliance on rules.',
      3: 'Real-world safety comes from combining awareness, communication, and judgment.',
    };
    return principles[phase] || principles[1];
  }

  /**
   * Add a clickable text link for secondary actions
   */
  private addActionLink(text: string, y: number, onClick: () => void): void {
    const link = new Text({
      text,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fill: theme.colors.textMuted,
      }),
    });
    link.anchor.set(0.5, 0);
    link.position.set(0, y);
    link.eventMode = 'static';
    link.cursor = 'pointer';
    link.on('pointerover', () => (link.style.fill = theme.colors.decision));
    link.on('pointerout', () => (link.style.fill = theme.colors.textMuted));
    link.on('pointerdown', onClick);
    this.content.addChild(link);
  }

  private handleRevisit(): void {
    stateMachine.clearHistory();
    app.navigateTo('scenario');
  }

  private handleContinue(): void {
    const session = stateMachine.getSessionState();

    if (session.currentPhase < 3) {
      stateMachine.advancePhase();
      app.navigateTo('scenario');
    } else {
      // Already at phase 3, this shouldn't happen but handle gracefully
      stateMachine.resetSession();
      app.navigateTo('landing');
    }
  }

  private positionContent(): void {
    this.content.position.set(this.context.width / 2, 50 - this.scrollOffset);
  }

  protected handleResize(): void {
    const bg = this.children[0] as Graphics;
    bg.clear();
    bg.rect(0, 0, this.context.width, this.context.height);
    bg.fill(theme.colors.primaryBg);

    this.positionContent();
  }
}
